import type { VendorDetailItem } from '@/services/vendorDetails'
import type { MultiSelectOption } from '@/components/MultiSelectFilter'
import type { FilterPill } from '@/tableControls'
import { foldForSearch } from '@/utils/foldForSearch'
import { formatAddress } from '@/pages/vendorDetailsTableConfig'

export const VENDOR_FILTER_NOT_SET = '__not_set__'

export interface VendorTableFilters {
  display_name: string[]
  active: string[]
  marketplace: string[]
  payment_method: string[]
  liability_timing: string[]
  term_ref: string[]
}

export const EMPTY_VENDOR_TABLE_FILTERS: VendorTableFilters = {
  display_name: [],
  active: [],
  marketplace: [],
  payment_method: [],
  liability_timing: [],
  term_ref: [],
}

/** Default on first load (Status = Active only). Clear filters resets to empty. */
export const DEFAULT_VENDOR_TABLE_FILTERS: VendorTableFilters = {
  ...EMPTY_VENDOR_TABLE_FILTERS,
  active: ['Active'],
}

/** Active when Gross Spend LTM is greater than zero (not QBO vendor Active flag). */
export function isVendorLtmActive(vendor: VendorDetailItem): boolean {
  return vendor.gross_spend_ltm > 0
}

export function vendorLtmStatusLabel(vendor: VendorDetailItem): 'Active' | 'Inactive' {
  return isVendorLtmActive(vendor) ? 'Active' : 'Inactive'
}

function fieldFilterValue(raw: string): string {
  const trimmed = raw.trim()
  return trimmed || VENDOR_FILTER_NOT_SET
}

function matchesSelected(value: string, selected: string[]): boolean {
  return selected.length === 0 || selected.includes(value)
}

export function hasActiveVendorTableFilters(filters: VendorTableFilters): boolean {
  return countActiveVendorFilters(filters) > 0
}

export function countActiveVendorFilters(filters: VendorTableFilters): number {
  return Object.values(filters).reduce((sum, values) => sum + values.length, 0)
}

const VENDOR_FILTER_FIELD_LABELS: Record<keyof VendorTableFilters, string> = {
  display_name: 'Vendor name',
  active: 'Status',
  marketplace: 'Marketplace',
  payment_method: 'Payment method',
  liability_timing: 'Liability timing',
  term_ref: 'Payment terms',
}

function formatVendorFilterValue(value: string): string {
  return value === VENDOR_FILTER_NOT_SET ? '(Not set)' : value
}

/** Pill adapter for ActiveFilterPillBar (multi-select filters, not FilterTree). */
export function generateVendorFilterPills(filters: VendorTableFilters): FilterPill[] {
  const pills: FilterPill[] = []
  for (const key of Object.keys(VENDOR_FILTER_FIELD_LABELS) as (keyof VendorTableFilters)[]) {
    for (const value of filters[key]) {
      pills.push({
        id: `vendor-filter-${key}::${encodeURIComponent(value)}`,
        nodeId: `${key}::${value}`,
        label: `${VENDOR_FILTER_FIELD_LABELS[key]}: ${formatVendorFilterValue(value)}`,
      })
    }
  }
  return pills
}

export function removeVendorFilterPill(
  filters: VendorTableFilters,
  pillId: string
): VendorTableFilters {
  const match = pillId.match(/^vendor-filter-([^:]+)::(.+)$/)
  if (!match) return filters
  const [, rawKey, encodedValue] = match
  if (!rawKey || encodedValue === undefined) return filters
  const key = rawKey as keyof VendorTableFilters
  if (!(key in filters)) return filters
  const value = decodeURIComponent(encodedValue)
  return {
    ...filters,
    [key]: filters[key].filter((item) => item !== value),
  }
}

export function applyVendorTableFilters(
  vendors: VendorDetailItem[],
  filters: VendorTableFilters
): VendorDetailItem[] {
  return vendors.filter((vendor) => {
    if (!matchesSelected(vendor.display_name, filters.display_name)) return false
    if (!matchesSelected(vendorLtmStatusLabel(vendor), filters.active)) return false
    if (!matchesSelected(vendor.marketplace, filters.marketplace)) {
      return false
    }
    if (!matchesSelected(fieldFilterValue(vendor.payment_method), filters.payment_method)) {
      return false
    }
    if (!matchesSelected(fieldFilterValue(vendor.liability_timing), filters.liability_timing)) {
      return false
    }
    if (!matchesSelected(fieldFilterValue(vendor.term_ref), filters.term_ref)) return false
    return true
  })
}

function vendorSearchHaystack(vendor: VendorDetailItem): string {
  return [
    vendor.display_name,
    vendor.company_name,
    vendor.print_on_check_name,
    vendor.payment_method,
    vendor.overseen_by,
    vendor.liability_timing,
    vendor.term_ref,
    vendor.primary_email,
    vendor.primary_phone,
    vendor.mobile,
    vendor.notes,
    vendor.tax_identifier,
    vendor.acct_num,
    vendor.web_url,
    formatAddress(vendor),
  ]
    .filter(Boolean)
    .join(' ')
}

export function filterVendorsByText(
  vendors: VendorDetailItem[],
  query: string
): VendorDetailItem[] {
  const foldedQuery = foldForSearch(query.trim())
  if (!foldedQuery) return vendors
  return vendors.filter((vendor) =>
    foldForSearch(vendorSearchHaystack(vendor)).includes(foldedQuery)
  )
}

function uniqueStringOptions(
  vendors: VendorDetailItem[],
  getter: (vendor: VendorDetailItem) => string
): MultiSelectOption[] {
  const values = new Set<string>()
  for (const vendor of vendors) {
    values.add(fieldFilterValue(getter(vendor)))
  }
  return Array.from(values)
    .sort((a, b) => {
      if (a === VENDOR_FILTER_NOT_SET) return 1
      if (b === VENDOR_FILTER_NOT_SET) return -1
      return a.localeCompare(b, undefined, { sensitivity: 'base' })
    })
    .map((id) => ({
      id,
      label: id === VENDOR_FILTER_NOT_SET ? '(Not set)' : id,
    }))
}

const MARKETPLACE_FILTER_ORDER = ['None', 'Airgoods', 'Faire'] as const

export function buildVendorFilterOptions(vendors: VendorDetailItem[]) {
  const statusOptions: MultiSelectOption[] = []
  if (vendors.some((v) => isVendorLtmActive(v))) statusOptions.push({ id: 'Active', label: 'Active' })
  if (vendors.some((v) => !isVendorLtmActive(v))) {
    statusOptions.push({ id: 'Inactive', label: 'Inactive' })
  }

  const marketplaceOptions: MultiSelectOption[] = MARKETPLACE_FILTER_ORDER.map((id) => ({
    id,
    label: id,
  }))

  return {
    display_name: Array.from(new Set(vendors.map((v) => v.display_name)))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map((name) => ({ id: name, label: name })),
    active: statusOptions,
    marketplace: marketplaceOptions,
    payment_method: uniqueStringOptions(vendors, (v) => v.payment_method),
    liability_timing: uniqueStringOptions(vendors, (v) => v.liability_timing),
    term_ref: uniqueStringOptions(vendors, (v) => v.term_ref),
  }
}
